import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import BeachConditionsMap from '../../components/beach-conditions/BeachConditionsMap';
import AppLink from '../../components/shared/AppLink';
import TripPlannerPromoHero from '../../components/shared/TripPlannerPromoHero';
import { getBeachConditionsIndex } from '../../services/beach-conditions/getBeachConditions';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import { buildRuntimeCanonicalUrl } from '../../lib/siteUrls';
import { getIslandRegionDefinitions } from 'alakai-aloha-shared';

const STATUS_META = {
  favorable_with_caution: {
    label: 'Favorable',
    shortLabel: 'Favorable',
    className: 'bg-emerald-500/20 text-emerald-200 border-emerald-300/30',
  },
  use_extra_caution: {
    label: 'Use Extra Caution',
    shortLabel: 'Caution',
    className: 'bg-amber-500/20 text-amber-200 border-amber-300/30',
  },
  not_recommended_today: {
    label: 'Not Recommended Today',
    shortLabel: 'Not Recommended',
    className: 'bg-red-500/25 text-red-100 border-red-300/40',
  },
  insufficient_current_data: {
    label: 'Insufficient Current Data',
    shortLabel: 'Data Limited',
    className: 'bg-slate-500/20 text-slate-200 border-slate-300/30',
  },
};

const LAND_ACTIVITY_KEYS = new Set(['shore_walk', 'sunbathing']);
const LAND_SAFETY_REASON_TOKENS = [
  'HIGH_SURF',
  'LARGE_WAVE',
  'HIGH_TIDE',
  'RUNUP',
  'THUNDERSTORM',
  'FLOOD',
  'HIGH_WIND',
];

const ACTIVITY_OPTIONS = [
  { value: 'swimming', label: 'Swimming' },
  { value: 'wading', label: 'Wading' },
  { value: 'shore_walk', label: 'Walking Along Shore' },
  { value: 'snorkeling', label: 'Snorkeling' },
  { value: 'surfing', label: 'Wave Riding' },
  { value: 'sunbathing', label: 'Sunbathing' },
];

const ACTIVITY_ROUTE_SLUG_BY_KEY = {
  swimming: 'swimming',
  wading: 'wading',
  shore_walk: 'walking-along-shore',
  snorkeling: 'snorkeling',
  surfing: 'wave-riding',
  sunbathing: 'sunbathing',
};

const ACTIVITY_KEY_BY_ROUTE_SLUG = Object.fromEntries(
  Object.entries(ACTIVITY_ROUTE_SLUG_BY_KEY).map(([key, slug]) => [slug, key]),
);

const TOP_ACTIVITY_RESULTS = 10;

function renderUpdatedAt(isoString) {
  if (!isoString) return 'Unknown';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function normalizeSlug(value) {
  if (!value) return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .trim();
}

function normalizeRegionValue(value) {
  if (!value) return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .trim();
}

function parseRegionFromSearch(search, regionValues) {
  const params = new URLSearchParams(search || '');
  const normalized = normalizeRegionValue(params.get('region') || '');
  return regionValues.has(normalized) ? normalized : '';
}

function regionForBeach(beach) {
  return normalizeRegionValue(beach?.region || '') || null;
}

function activityMeta(activityKey) {
  return ACTIVITY_OPTIONS.find((item) => item.value === activityKey) || null;
}

function getActivityRecommendation(beach, activityKey) {
  if (!activityKey) return null;
  return (beach?.summary?.activities || []).find((entry) => entry.key === activityKey) || null;
}

function hasLandSafetyReason(reasonCodes = []) {
  return reasonCodes.some((code) =>
    LAND_SAFETY_REASON_TOKENS.some((token) => String(code || '').includes(token)));
}

function statusMetaForActivity(status, activityKey, reasonCodes = []) {
  const base = STATUS_META[status] || STATUS_META.insufficient_current_data;
  if (!LAND_ACTIVITY_KEYS.has(activityKey)) return base;

  if (status === 'favorable_with_caution') {
    return { ...base, label: 'Favorable', shortLabel: 'Favorable' };
  }

  if (status === 'use_extra_caution') {
    if (hasLandSafetyReason(reasonCodes)) {
      return { ...base, label: 'Use Caution', shortLabel: 'Caution' };
    }
    return { ...base, label: 'Fair Conditions', shortLabel: 'Fair' };
  }

  if (status === 'not_recommended_today' && !hasLandSafetyReason(reasonCodes)) {
    return { ...base, label: 'Poor Conditions', shortLabel: 'Poor' };
  }

  return base;
}

function getActivityScore(beach, activityKey) {
  const rec = getActivityRecommendation(beach, activityKey);
  if (!rec || !Number.isFinite(rec.score)) return null;
  return rec.score;
}

function rankBeachesForActivity(beaches, activityKey, topN = TOP_ACTIVITY_RESULTS) {
  if (!activityKey) return beaches;
  const sorted = [...beaches].sort((a, b) => {
    const scoreA = getActivityScore(a, activityKey);
    const scoreB = getActivityScore(b, activityKey);
    const normalizedA = scoreA === null ? -1 : scoreA;
    const normalizedB = scoreB === null ? -1 : scoreB;
    if (normalizedB !== normalizedA) return normalizedB - normalizedA;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
  return sorted.slice(0, topN);
}

export default function BeachConditionsIndex({ activitySlug = '' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { island, islandConfig } = useMemo(() => getRuntimeSiteContext(), []);
  const allRegionOptions = useMemo(
    () => getIslandRegionDefinitions(island).map((region) => ({
      value: region.key,
      label: region.title,
    })),
    [island],
  );
  const allRegionValues = useMemo(() => new Set(allRegionOptions.map((option) => option.value)), [allRegionOptions]);

  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [activitySummaryPayload, setActivitySummaryPayload] = useState(null);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [visibleSlugs, setVisibleSlugs] = useState(null);

  const routeActivityKey = useMemo(() => {
    const normalized = normalizeSlug(activitySlug);
    return ACTIVITY_KEY_BY_ROUTE_SLUG[normalized] || '';
  }, [activitySlug]);

  const [selectedRegion, setSelectedRegion] = useState(() => parseRegionFromSearch(location.search, allRegionValues));
  const [selectedActivity, setSelectedActivity] = useState(routeActivityKey);

  useEffect(() => {
    setSelectedActivity((prev) => (prev === routeActivityKey ? prev : routeActivityKey));
  }, [routeActivityKey]);

  useEffect(() => {
    const nextPath = selectedActivity
      ? `/beach-conditions/${ACTIVITY_ROUTE_SLUG_BY_KEY[selectedActivity]}`
      : '/beach-conditions';
    const params = new URLSearchParams();
    if (selectedRegion) params.set('region', selectedRegion);
    const nextSearch = params.toString() ? `?${params.toString()}` : '';
    const nextUrl = `${nextPath}${nextSearch}`;
    const currentUrl = `${location.pathname}${location.search}`;

    if (nextUrl !== currentUrl) {
      navigate(nextUrl, { replace: true });
    }
  }, [selectedActivity, selectedRegion, navigate, location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        setLoading(true);
        setError('');
        const data = await getBeachConditionsIndex({ island });
        if (cancelled) return;
        setPayload(data);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load beach conditions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [island]);

  const beaches = useMemo(
    () => (Array.isArray(payload?.beaches) ? payload.beaches : []).map((beach) => ({
      ...beach,
      regionKey: regionForBeach(beach),
    })),
    [payload],
  );

  const availableRegionValues = useMemo(
    () => new Set(beaches.map((beach) => beach.regionKey).filter(Boolean)),
    [beaches],
  );

  const regionOptions = useMemo(
    () => allRegionOptions.filter((option) => availableRegionValues.has(option.value)),
    [allRegionOptions, availableRegionValues],
  );

  const regionValues = useMemo(
    () => (beaches.length ? new Set(regionOptions.map((option) => option.value)) : allRegionValues),
    [allRegionValues, beaches.length, regionOptions],
  );

  useEffect(() => {
    const regionFromQuery = parseRegionFromSearch(location.search, regionValues);
    setSelectedRegion((prev) => (prev === regionFromQuery ? prev : regionFromQuery));
  }, [location.search, regionValues]);

  const regionScopedBeaches = useMemo(() => {
    if (!selectedRegion) return beaches;
    return beaches.filter((beach) => beach.regionKey === selectedRegion);
  }, [beaches, selectedRegion]);

  useEffect(() => {
    if (!selectedActivity) {
      setActivitySummaryPayload(null);
      return;
    }

    let cancelled = false;

    async function loadActivitySummary() {
      try {
        const data = await getBeachConditionsIndex({
          mode: 'activity_ranking',
          activity: selectedActivity,
          region: selectedRegion || undefined,
          limit: TOP_ACTIVITY_RESULTS,
          island,
        });
        if (!cancelled) {
          setActivitySummaryPayload(data?.activityRankingSummary || null);
        }
      } catch {
        if (!cancelled) {
          setActivitySummaryPayload(null);
        }
      }
    }

    loadActivitySummary();
    return () => {
      cancelled = true;
    };
  }, [selectedActivity, selectedRegion, island]);

  const preViewportBeaches = useMemo(() => {
    if (selectedActivity) {
      return rankBeachesForActivity(regionScopedBeaches, selectedActivity, TOP_ACTIVITY_RESULTS);
    }

    return regionScopedBeaches;
  }, [regionScopedBeaches, selectedActivity]);

  const preViewportKey = useMemo(
    () => preViewportBeaches.map((beach) => beach.slug).join('|'),
    [preViewportBeaches],
  );

  useEffect(() => {
    setVisibleSlugs(null);
  }, [preViewportKey]);

  useEffect(() => {
    if (preViewportBeaches.length === 0) {
      setSelectedSlug('');
      return;
    }
    if (selectedSlug && !preViewportBeaches.some((beach) => beach.slug === selectedSlug)) {
      setSelectedSlug('');
    }
  }, [preViewportBeaches, selectedSlug]);

  const selectedBeach = preViewportBeaches.find((beach) => beach.slug === selectedSlug) || null;
  const visibleBeachSet = useMemo(
    () => (Array.isArray(visibleSlugs) ? new Set(visibleSlugs) : null),
    [visibleSlugs],
  );

  const viewportBeaches = useMemo(() => {
    if (!visibleBeachSet) return preViewportBeaches;
    return preViewportBeaches.filter((beach) => visibleBeachSet.has(beach.slug));
  }, [preViewportBeaches, visibleBeachSet]);

  const displayedBeaches = viewportBeaches;

  const globalActivityRankBySlug = useMemo(() => {
    if (!selectedActivity) return new Map();
    const ranked = rankBeachesForActivity(beaches, selectedActivity, beaches.length || TOP_ACTIVITY_RESULTS);
    const map = new Map();
    ranked.forEach((beach, index) => {
      map.set(beach.slug, index + 1);
    });
    return map;
  }, [beaches, selectedActivity]);

  const resolvedActivityExplainer = selectedActivity
    ? (activitySummaryPayload?.text || '')
    : '';

  const activeActivityMeta = activityMeta(selectedActivity);

  const selectedBeachActivityRecommendation = useMemo(
    () => (
      selectedBeach && selectedActivity
        ? getActivityRecommendation(selectedBeach, selectedActivity)
        : null
    ),
    [selectedBeach, selectedActivity],
  );

  const allActivityResultsChallenging = Boolean(
    selectedActivity &&
      preViewportBeaches.length > 0 &&
      preViewportBeaches.every((beach) => {
        const score = getActivityScore(beach, selectedActivity);
        return !Number.isFinite(score) || score < 45;
      }),
  );
  const toggleBeachOnMap = (slugToToggle) => {
    if (!slugToToggle) return;
    setSelectedSlug((prev) => (prev === slugToToggle ? '' : slugToToggle));
    if (typeof document !== 'undefined') {
      const mapEl = document.getElementById('beach-conditions-map');
      mapEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const canonicalPath = selectedActivity
    ? `/beach-conditions/${ACTIVITY_ROUTE_SLUG_BY_KEY[selectedActivity]}`
    : '/beach-conditions';

  return (
    <>
      <Helmet>
        <title>Beach Conditions | Alakaʻi Aloha</title>
        <meta
          name="description"
          content={`Check current ${islandConfig.title} beach conditions with activity-specific guidance for swimming, snorkeling, surfing, shoreline walks, and sunbathing.`}
        />
        <link rel="canonical" href={buildRuntimeCanonicalUrl(canonicalPath)} />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 pt-2 pb-10 space-y-8">
        <header className="space-y-3">
          <h1 className="text-4xl font-bold">Beach Conditions</h1>
          <p className="text-white/80">
            This page focuses on today&apos;s suitability for beach activities. For{' '}
            <Link to="/activities?category=Beach" className="underline hover:text-green-300">
              beach overviews
            </Link>{' '}
            and long-term planning, use the linked beach profiles.
          </p>
        </header>

        {loading ? (
          <div className="rounded-lg border border-white/15 bg-white/5 px-4 py-8 text-white/70">
            Loading beach conditions...
          </div>
        ) : null}

        {!loading && error ? (
          <div className="rounded-lg border border-red-300/50 bg-red-900/20 px-4 py-8 text-red-100">
            <p className="font-semibold">Beach conditions are currently unavailable.</p>
            <p className="mt-2 text-sm">{error}</p>
          </div>
        ) : null}

        {!loading && !error ? (
          <>
            <section className="space-y-4">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-sm sm:text-base">
                  <span className="text-white/90">Show me which beaches are best for</span>
                  <select
                    value={selectedActivity}
                    onChange={(event) => setSelectedActivity(event.target.value)}
                    className="rounded-md border border-white/25 bg-white/10 backdrop-blur-sm px-3 py-2 text-sm text-white"
                  >
                    <option value="" style={{ color: '#111827', backgroundColor: '#ffffff' }}>
                      any activity
                    </option>
                    {ACTIVITY_OPTIONS.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className="text-white/90">today.</span>
                </div>

                <div className="sm:hidden space-y-1">
                  <label htmlFor="region-filter-mobile" className="block text-sm text-white/75">
                    Narrow to beaches.
                  </label>
                  <select
                    id="region-filter-mobile"
                    value={selectedRegion}
                    onChange={(event) => setSelectedRegion(event.target.value)}
                    className="w-full rounded-md border border-white/25 bg-white/10 backdrop-blur-sm px-3 py-2 text-sm text-white"
                  >
                    <option value="" style={{ color: '#111827', backgroundColor: '#ffffff' }}>
                      All regions
                    </option>
                    {regionOptions.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hidden sm:flex flex-wrap items-center gap-2 text-sm sm:text-base">
                  <span className="text-white/75">Narrow to</span>
                  {regionOptions.map((option) => {
                    const isSelected = selectedRegion === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSelectedRegion((prev) => (prev === option.value ? '' : option.value))}
                        className={`rounded-full border px-3 py-1.5 text-sm sm:text-base transition ${
                          isSelected
                            ? 'border-emerald-300/70 bg-emerald-500/15 text-emerald-100'
                            : 'border-white/20 bg-white/[0.04] text-white/75 hover:border-white/40 hover:text-white'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                  <span className="text-white/75">beaches.</span>
                </div>

                {allActivityResultsChallenging ? (
                  <div className="rounded-md border border-amber-300/45 bg-amber-900/25 px-3 py-2 text-sm text-amber-100">
                    Current conditions are challenging across this set. These are the least-risk options, not strong “go now” recommendations.
                  </div>
                ) : null}
              </div>

              <div id="beach-conditions-map" className="scroll-mt-24">
                <BeachConditionsMap
                  island={island}
                  beaches={preViewportBeaches}
                  selectedSlug={selectedSlug}
                  onSelectBeach={setSelectedSlug}
                  onVisibleBeachesChange={setVisibleSlugs}
                  rankBySlug={selectedActivity ? globalActivityRankBySlug : null}
                />
              </div>
              {selectedBeach ? (
                <article className="rounded-lg border border-emerald-300/45 bg-emerald-900/15 p-3">
                  <div className="flex items-start gap-3">
                    {selectedBeach.imageUrl ? (
                      <img
                        src={selectedBeach.imageUrl}
                        alt={`${selectedBeach.name} shoreline`}
                        className="h-14 w-20 shrink-0 rounded-md border border-emerald-200/45 object-cover"
                      />
                    ) : (
                      <div className="h-14 w-20 shrink-0 rounded-md border border-emerald-200/35 bg-emerald-900/15 text-[11px] text-emerald-100/80 grid place-items-center">
                        No Image
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] uppercase tracking-wide text-emerald-100/85">Selected Beach</div>
                      <h3 className="text-lg font-semibold">{selectedBeach.name}</h3>
                      {selectedBeach.area ? (
                        <div className="text-[11px] uppercase tracking-wide text-white/65">{selectedBeach.area}</div>
                      ) : null}
                    </div>
                    {selectedBeachActivityRecommendation && activeActivityMeta ? (
                      <div className="text-right text-xs">
                        <div className="text-white/70">{activeActivityMeta.label}</div>
                        <div className="font-semibold text-white">
                          {Number.isFinite(selectedBeachActivityRecommendation.score)
                            ? `${selectedBeachActivityRecommendation.score}/100`
                            : '—'}
                        </div>
                        <span
                          className={`mt-1 inline-block rounded-full border px-2 py-0.5 text-[10px] ${
                            statusMetaForActivity(
                              selectedBeachActivityRecommendation.status,
                              selectedActivity,
                              selectedBeachActivityRecommendation.reasonCodes,
                            ).className
                          }`}
                        >
                          {statusMetaForActivity(
                            selectedBeachActivityRecommendation.status,
                            selectedActivity,
                            selectedBeachActivityRecommendation.reasonCodes,
                          ).shortLabel}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  {selectedBeach?.summary?.dailySummary ? (
                    <p className="mt-2 text-sm text-white/85">{selectedBeach.summary.dailySummary}</p>
                  ) : null}
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                    <button
                      type="button"
                      onClick={() => setSelectedSlug('')}
                      className="inline-flex items-center gap-1 rounded-full border border-emerald-200/45 bg-emerald-900/35 px-2 py-0.5 text-xs text-emerald-100 hover:bg-emerald-900/60"
                      aria-label="Dismiss selected beach"
                    >
                      <span aria-hidden="true">×</span>
                      <span>Dismiss</span>
                    </button>
                    <Link to={`/beach-conditions/${selectedBeach.slug}`} className="underline hover:text-green-300">
                      View full report
                    </Link>
                    <AppLink to={`/activities/${selectedBeach.slug}`} className="underline hover:text-green-300">
                      View beach profile
                    </AppLink>
                  </div>
                </article>
              ) : null}

              {selectedActivity && resolvedActivityExplainer ? (
                <div className="rounded-md border border-emerald-300/40 bg-emerald-900/20 px-3 py-2 text-sm text-emerald-100">
                  {resolvedActivityExplainer}
                  {activitySummaryPayload?.generatedAt ? (
                    <div className="mt-1 text-[11px] text-emerald-100/80">
                      Updated: {renderUpdatedAt(activitySummaryPayload.generatedAt)}
                    </div>
                  ) : null}
                  {activitySummaryPayload?.error ? (
                    <div className="mt-1 text-[11px] text-amber-200/90">
                      LLM ranking narrative unavailable in the latest report: {activitySummaryPayload.error}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>

            <section className="space-y-4">
              {displayedBeaches.length === 0 ? (
                <div className="rounded-lg border border-white/15 bg-white/5 px-4 py-6 text-sm text-white/70">
                  No mapped beaches are in the current viewport. Zoom out or pan to see beach cards.
                </div>
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {displayedBeaches.map((beach) => {
                  const isSelected = beach.slug === selectedSlug;
                  const activityRank = selectedActivity ? (globalActivityRankBySlug.get(beach.slug) || null) : null;
                  const dailySummary = beach?.summary?.dailySummary || '';
                  const webcams = Array.isArray(beach?.webcams) ? beach.webcams : [];
                  const primaryWebcam = webcams[0] || null;
                  const hasSelectedActivity = Boolean(selectedActivity);
                  const activityOptionsForCard = hasSelectedActivity
                    ? ACTIVITY_OPTIONS.filter((activityOption) => activityOption.value === selectedActivity)
                    : ACTIVITY_OPTIONS;

                  const activityRows = activityOptionsForCard.map((activityOption) => {
                    const rec = getActivityRecommendation(beach, activityOption.value);
                    const score = Number.isFinite(rec?.score) ? rec.score : null;
                    const statusMeta = statusMetaForActivity(
                      rec?.status,
                      activityOption.value,
                      rec?.reasonCodes,
                    );
                    return {
                      key: activityOption.value,
                      label: activityOption.label,
                      score,
                      statusMeta,
                      isFocused: hasSelectedActivity,
                    };
                  });

                  return (
                    <article
                      key={beach.slug}
                      className={`relative h-full rounded-lg border p-4 transition cursor-default flex flex-col ${
                        isSelected
                          ? 'border-emerald-300/70 bg-white/[0.14] backdrop-blur-md ring-1 ring-emerald-400/45'
                          : 'border-white/20 bg-white/[0.08] backdrop-blur-md hover:border-white/35 hover:bg-white/[0.12]'
                      }`}
                    >
                      {hasSelectedActivity && activityRank ? (
                        <div className="absolute right-3 top-3 rounded-full border border-emerald-300/60 bg-emerald-900/40 px-2 py-0.5 text-xs font-semibold text-emerald-100">
                          #{activityRank}
                        </div>
                      ) : null}
                      <div className="flex items-start gap-3">
                        <Link to={`/beach-conditions/${beach.slug}`} className="shrink-0">
                          {beach.imageUrl ? (
                            <img
                              src={beach.imageUrl}
                              alt={`${beach.name} shoreline`}
                              loading="lazy"
                              className={`h-16 w-24 rounded-md object-cover border ${
                                isSelected ? 'border-emerald-300/70' : 'border-white/20'
                              }`}
                            />
                          ) : (
                            <div
                              className={`h-16 w-24 rounded-md border text-[11px] grid place-items-center text-white/50 ${
                                isSelected ? 'border-emerald-300/55 bg-white/[0.12]' : 'border-white/25 bg-white/[0.08]'
                              }`}
                            >
                              No Image
                            </div>
                          )}
                        </Link>

                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl font-semibold">
                            <Link to={`/beach-conditions/${beach.slug}`} className="hover:text-emerald-100">
                              {beach.name}
                            </Link>
                          </h3>

                          {beach.area ? (
                            <div className="mt-1 text-xs uppercase tracking-wide text-white/60">{beach.area}</div>
                          ) : null}

                          {primaryWebcam ? (
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                              <span className="rounded-full border border-emerald-300/45 bg-emerald-900/25 px-2 py-0.5 text-emerald-100">
                                {primaryWebcam.nearby ? 'Nearby Live Webcam' : 'Live Webcam'}
                              </span>
                              <a
                                href={primaryWebcam.pageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => event.stopPropagation()}
                                className="underline text-white/80 hover:text-green-300"
                              >
                                Open Cam
                              </a>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {dailySummary ? (
                        <p className="mt-3 text-sm text-white/80">{dailySummary}</p>
                      ) : (
                        <div className="mt-3 rounded-md border border-amber-300/40 bg-amber-900/20 px-3 py-2 text-xs text-amber-100">
                          Daily narrative is unavailable for this beach in the latest conditions run.
                        </div>
                      )}

                      <div className="mt-auto pt-3">
                        <div className={`grid gap-2 ${hasSelectedActivity ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
                          {activityRows.map((activityRow) => (
                            <div
                              key={`${beach.slug}-${activityRow.key}`}
                              className={`rounded-lg border px-2 py-2 ${
                                activityRow.isFocused
                                  ? 'border-emerald-300/60 bg-emerald-900/20'
                                  : 'border-white/15 bg-white/[0.03]'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2 text-xs">
                                <span className="font-semibold text-white/90">{activityRow.label}</span>
                                <span className="font-semibold text-white">
                                  {activityRow.score !== null ? `${activityRow.score}/100` : '—'}
                                </span>
                              </div>
                              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                                <div
                                  className={`h-full ${
                                    activityRow.score === null
                                      ? 'bg-white/30'
                                      : activityRow.score >= 70
                                        ? 'bg-emerald-300'
                                        : activityRow.score >= 45
                                          ? 'bg-amber-300'
                                          : 'bg-red-300'
                                  }`}
                                  style={{ width: `${Math.max(0, Math.min(100, activityRow.score ?? 0))}%` }}
                                />
                              </div>
                              <div className="mt-1 flex items-center justify-end">
                                <span
                                  className={`rounded-full border px-1.5 py-0.5 text-[10px] ${activityRow.statusMeta.className}`}
                                  title={activityRow.statusMeta.label}
                                >
                                  {activityRow.statusMeta.shortLabel}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-4 grid grid-cols-3 gap-2 text-xs sm:text-sm">
                          <button
                            type="button"
                            onClick={() => toggleBeachOnMap(beach.slug)}
                            className={`inline-flex items-center justify-center whitespace-nowrap rounded-full border px-2 py-1 ${
                              isSelected
                                ? 'border-emerald-300/60 bg-emerald-900/35 text-emerald-100 hover:bg-emerald-900/55'
                                : 'border-white/30 bg-white/[0.06] text-white/90 hover:bg-white/[0.12]'
                            }`}
                          >
                            <span className="sm:hidden">{isSelected ? 'Hide' : 'Map'}</span>
                            <span className="hidden sm:inline">{isSelected ? 'Hide on map' : 'Show on map'}</span>
                          </button>
                          <Link
                            to={`/beach-conditions/${beach.slug}`}
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-emerald-300/60 bg-emerald-900/30 px-2 py-1 font-semibold text-emerald-100 hover:bg-emerald-900/45"
                          >
                            <span className="sm:hidden">Report</span>
                            <span className="hidden sm:inline">Full report</span>
                          </Link>
                          <AppLink
                            to={`/activities/${beach.slug}`}
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-white/30 bg-white/[0.06] px-2 py-1 text-white/90 hover:bg-white/[0.12]"
                          >
                            <span className="sm:hidden">Profile</span>
                            <span className="hidden sm:inline">Beach profile</span>
                          </AppLink>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          </>
        ) : null}
      </div>

      <TripPlannerPromoHero pageType="activity" className="mt-8" />
    </>
  );
}
